import { create } from 'zustand';

export interface AuthUser {
  id: string;
  fullName: string;
  role: string;
  photoUrl?: string | null;
  mustChangePassword?: boolean;
}

// ─── Token storage ────────────────────────────────────────────────────────────
// Access token lives in memory only (not persisted to localStorage)
let _accessToken: string | null = null;
const REFRESH_TOKEN_KEY = 'refreshToken';
const OFFLINE_USER_KEY = 'heimsOfflineUser';

export function setTokens(access: string, refresh: string): void {
  _accessToken = access;
  localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
}

export function clearTokens(): void {
  _accessToken = null;
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  clearOfflineUser();
}

export function getAccessToken(): string | null {
  return _accessToken;
}

export function getRefreshToken(): string | null {
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

export function saveOfflineUser(user: AuthUser): void {
  localStorage.setItem(OFFLINE_USER_KEY, JSON.stringify(user));
}

export function getOfflineUser(): AuthUser | null {
  const rawUser = localStorage.getItem(OFFLINE_USER_KEY);
  if (!rawUser) return null;

  try {
    return JSON.parse(rawUser) as AuthUser;
  } catch {
    localStorage.removeItem(OFFLINE_USER_KEY);
    return null;
  }
}

export function clearOfflineUser(): void {
  localStorage.removeItem(OFFLINE_USER_KEY);
}

export function hasOfflineSession(): boolean {
  return Boolean(getRefreshToken() && getOfflineUser());
}

// ─── Auth store ───────────────────────────────────────────────────────────────
interface AuthState {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isOfflineSession: boolean;
  setUser: (user: AuthUser) => void;
  setOfflineUser: (user: AuthUser) => void;
  clearUser: () => void;
}

export const useAuthStore = create<AuthState>((set) => ({
  user: null,
  isAuthenticated: false,
  isOfflineSession: false,
  setUser: (user) => set({ user, isAuthenticated: true, isOfflineSession: false }),
  setOfflineUser: (user) => set({ user, isAuthenticated: true, isOfflineSession: true }),
  clearUser: () => set({ user: null, isAuthenticated: false, isOfflineSession: false }),
}));

// ─── useAuth hook ─────────────────────────────────────────────────────────────
export function useAuth() {
  return useAuthStore();
}
